import React, { useEffect, useCallback } from "react";
import { withStyles } from "@mui/styles";
import { formatISO } from "date-fns";
import { subMonths } from "date-fns";

import { withState } from "../../../services/state";
import {
  getDetailedPaymentReport as apiGetDetailedPaymentReport,
  getEventFeeAdjustmentsReport as apiGetEventFeeAdjustmentsReport,
  getParticipationReport as apiGetParticipationReport,
  getSummaryPaymentReport as apiGetSummaryPaymentReport,
  getEventTypes as apiGetEventTypes,
  getTeams as apiGetTeams,
} from "../../../services/api";

import PageWrapper from "../../components/pageWrapper/PageWrapper";
import Section from "../../components/section/Section";
import DropdownSelect from "../../components/dropdownSelect/DropdownSelect";

import DetailedPaymentReportTable from "../../tables/detailedPaymentReportTable/DetailedPaymentReportTable";
import SummaryPaymentReportTable from "../../tables/summaryPaymentReportTable/SummaryPaymentReportTable";
import ParticipationReportTable from "../../tables/participationReportTable/ParticipationReportTable";
import EventFeeAdjustmentsReportTable from "../../tables/eventFeeAdjustmentsReportTable/EventFeeAdjustmentsReportTable";

const styles = (theme) => ({
  root: {
    width: "100%",
  },
});

function ReportsPage({ context }) {
  // const [gettingClubReport, setGettingClubReport] = React.useState(false);
  const [detailedReportData, setDetailedReportData] = React.useState([]);
  const [filterReportStart, setFilterReportStart] = React.useState(
    subMonths(new Date(), 3)
  );
  const [filterReportEnd, setFilterReportEnd] = React.useState(new Date());
  const [summaryReportData, setSummaryReportData] = React.useState([]);
  const [participationReportData, setParticipationReportData] = React.useState(
    []
  );
  const [eventFeeAdjustmentsReportData, setEventFeeAdjustmentsReportData] =
    React.useState([]);
  const [eventTypes, setEventTypes] = React.useState([]);
  const [teams, setTeams] = React.useState([]);
  const [filterTeam, setFilterTeam] = React.useState(null);
  const [filterEventType, setFilterEventType] = React.useState(null);
  const [filterStatus, setFilterStatus] = React.useState(null);
  const [reportType, setReportType] = React.useState("detailedPaymentReport");

  const getDetailedPaymentReport = useCallback(async () => {
    if (
      reportType !== "detailedPaymentReport" ||
      !context.currentClub ||
      !context.currentClub.clubId ||
      !context.currentPlayer ||
      !context.currentPlayer.id
    ) {
      return false;
    }

    try {
      // setGettingClubReport(true);
      const data = await apiGetDetailedPaymentReport(
        context.currentClub.clubId,
        formatISO(new Date(filterReportStart), { representation: "date" }),
        formatISO(new Date(filterReportEnd), { representation: "date" }),
        {
          team: filterTeam,
          eventType: filterEventType,
          status: filterStatus,
        }
      );
      if (data && !data.reason) {
        setDetailedReportData(data);
      }
      // setGettingClubReport(false);
    } catch (e) {
      console.log(e);
      // setGettingClubReport(false);
    }
  }, [
    context.currentClub,
    context.currentPlayer,
    reportType,
    filterEventType,
    filterTeam,
    filterStatus,
    filterReportStart,
    filterReportEnd,
  ]);

  const getEventFeeAdjustmentsReport = useCallback(async () => {
    if (
      reportType !== "eventFeeAdjustmentsReport" ||
      !context.currentClub ||
      !context.currentClub.clubId ||
      !context.currentPlayer ||
      !context.currentPlayer.id
    ) {
      return false;
    }

    try {
      // setGettingClubReport(true);
      const data = await apiGetEventFeeAdjustmentsReport(
        context.currentClub.clubId,
        formatISO(new Date(filterReportStart), { representation: "date" }),
        formatISO(new Date(filterReportEnd), { representation: "date" }),
        {
          team: filterTeam,
          eventType: filterEventType,
          status: filterStatus,
        }
      );
      if (data && !data.reason) {
        setEventFeeAdjustmentsReportData(data);
      }
      // setGettingClubReport(false);
    } catch (e) {
      console.log(e);
      // setGettingClubReport(false);
    }
  }, [
    context.currentClub,
    context.currentPlayer,
    reportType,
    filterEventType,
    filterTeam,
    filterStatus,
    filterReportStart,
    filterReportEnd,
  ]);

  const getParticipationReport = useCallback(async () => {
    if (
      reportType !== "participationReport" ||
      !context.currentClub ||
      !context.currentClub.clubId ||
      !context.currentPlayer ||
      !context.currentPlayer.id
    ) {
      return false;
    }

    try {
      // setGettingClubReport(true);
      const data = await apiGetParticipationReport(
        context.currentClub.clubId,
        formatISO(new Date(filterReportStart), { representation: "date" }),
        formatISO(new Date(filterReportEnd), { representation: "date" }),
        {
          eventType: filterEventType,
        }
      );
      if (data && !data.reason) {
        setParticipationReportData(data);
      }
      // setGettingClubReport(false);
    } catch (e) {
      console.log(e);
      // setGettingClubReport(false);
    }
  }, [
    context.currentClub,
    context.currentPlayer,
    reportType,
    filterEventType,
    filterReportStart,
    filterReportEnd,
  ]);

  const getSummaryPaymentReport = useCallback(async () => {
    if (
      reportType !== "summaryPaymentReport" ||
      !context.currentClub ||
      !context.currentClub.clubId ||
      !context.currentPlayer ||
      !context.currentPlayer.id
    ) {
      return false;
    }

    try {
      // setGettingClubReport(true);
      const data = await apiGetSummaryPaymentReport(
        context.currentClub.clubId,
        formatISO(new Date(filterReportStart), { representation: "date" }),
        formatISO(new Date(filterReportEnd), { representation: "date" }),
        {
          team: filterTeam,
          eventType: filterEventType,
          status: filterStatus,
        }
      );
      if (data && !data.reason) {
        setSummaryReportData(data);
      }
      // setGettingClubReport(false);
    } catch (e) {
      console.log(e);
      // setGettingClubReport(false);
    }
  }, [
    context.currentClub,
    context.currentPlayer,
    reportType,
    filterEventType,
    filterTeam,
    filterStatus,
    filterReportStart,
    filterReportEnd,
  ]);

  const getEventTypes = useCallback(async () => {
    if (!context.currentClub.clubId) {
      return false;
    }

    try {
      const data = await apiGetEventTypes(context.currentClub.clubId);
      if (data) {
        setEventTypes(data);
      }
    } catch (e) {
      console.log(e);
    }
  }, [context.currentClub.clubId]);

  const getTeams = useCallback(async () => {
    if (
      !context.currentPlayer ||
      !context.currentPlayer.id ||
      !context.currentClub.clubId
    ) {
      return false;
    }

    try {
      const data = await apiGetTeams(context.currentClub.clubId);
      if (data && !data.reason) {
        setTeams(data);
      }
    } catch (e) {
      console.log(e);
    }
  }, [context.currentClub, context.currentPlayer]);

  const renderContent = () => {
    if (reportType === "detailedPaymentReport") {
      return (
        <DetailedPaymentReportTable
          detailedReportData={detailedReportData}
          showDateRangeFilter={true}
          dateRangeFilterStart={filterReportStart}
          onDateRangeFilterStartChange={(value) => {
            setFilterReportStart(value);
          }}
          dateRangeFilterEnd={filterReportEnd}
          onDateRangeFilterEndChange={(value) => {
            setFilterReportEnd(value);
          }}
          filterSelects={[
            {
              items: teams.map((team) => {
                return {
                  label: team.name,
                  value: team.id,
                };
              }),
              label: "Filter team",
              value: filterTeam,
              onSelect: (value) => {
                setFilterTeam(value);
              },
              placeholder: "All teams",
            },
            {
              items:
                eventTypes &&
                Array.isArray(eventTypes) &&
                eventTypes.map((eventType) => {
                  return {
                    label: eventType.name,
                    value: eventType.id,
                  };
                }),
              label: "Filter event type",
              value: filterEventType,
              onSelect: (value) => {
                setFilterEventType(value);
              },
              placeholder: "All event types",
            },
            {
              items: [
                { value: "paid", label: "Paid" },
                { value: "submitted", label: "Submitted" },
                { value: "owed", label: "Owed" },
              ],
              label: "Filter status",
              value: filterStatus,
              onSelect: (value) => {
                setFilterStatus(value);
              },
              placeholder: "Any status",
            },
          ]}
          showDownloadButton={true}
          downloadFilename="Slate Report"
        />
      );
    } else if (reportType === "summaryPaymentReport") {
      return (
        <SummaryPaymentReportTable
          summaryReportData={summaryReportData}
          showDateRangeFilter={true}
          dateRangeFilterStart={filterReportStart}
          onDateRangeFilterStartChange={(value) => {
            setFilterReportStart(value);
          }}
          dateRangeFilterEnd={filterReportEnd}
          onDateRangeFilterEndChange={(value) => {
            setFilterReportEnd(value);
          }}
          filterSelects={[
            {
              items: teams.map((team) => {
                return {
                  label: team.name,
                  value: team.id,
                };
              }),
              label: "Filter team",
              value: filterTeam,
              onSelect: (value) => {
                setFilterTeam(value);
              },
              placeholder: "All teams",
            },
            {
              items: eventTypes.map((eventType) => {
                return {
                  label: eventType.name,
                  value: eventType.id,
                };
              }),
              label: "Filter event type",
              value: filterEventType,
              onSelect: (value) => {
                setFilterEventType(value);
              },
              placeholder: "All event types",
            },
          ]}
          showDownloadButton={true}
          downloadFilename="Slate Report"
        />
      );
    } else if (reportType === "participationReport") {
      return (
        <ParticipationReportTable
          participationReportData={participationReportData}
          showDateRangeFilter={true}
          dateRangeFilterStart={filterReportStart}
          onDateRangeFilterStartChange={(value) => {
            setFilterReportStart(value);
          }}
          dateRangeFilterEnd={filterReportEnd}
          onDateRangeFilterEndChange={(value) => {
            setFilterReportEnd(value);
          }}
          showDownloadButton={true}
          downloadFilename="Participation Report"
          filterSelects={[
            {
              items:
                eventTypes &&
                Array.isArray(eventTypes) &&
                eventTypes.map((eventType) => {
                  return {
                    label: eventType.name,
                    value: eventType.id,
                  };
                }),
              label: "Filter event type",
              value: filterEventType,
              onSelect: (value) => {
                setFilterEventType(value);
              },
              placeholder: "All event types",
            },
          ]}
        />
      );
    } else if (reportType === "eventFeeAdjustmentsReport") {
      return (
        <EventFeeAdjustmentsReportTable
          eventFeeAdjustmentsReportData={eventFeeAdjustmentsReportData}
          showDateRangeFilter={true}
          dateRangeFilterStart={filterReportStart}
          onDateRangeFilterStartChange={(value) => {
            setFilterReportStart(value);
          }}
          dateRangeFilterEnd={filterReportEnd}
          onDateRangeFilterEndChange={(value) => {
            setFilterReportEnd(value);
          }}
          filterSelects={[
            {
              items: teams.map((team) => {
                return {
                  label: team.name,
                  value: team.id,
                };
              }),
              label: "Filter team",
              value: filterTeam,
              onSelect: (value) => {
                setFilterTeam(value);
              },
              placeholder: "All teams",
            },
            {
              items:
                eventTypes &&
                Array.isArray(eventTypes) &&
                eventTypes.map((eventType) => {
                  return {
                    label: eventType.name,
                    value: eventType.id,
                  };
                }),
              label: "Filter event type",
              value: filterEventType,
              onSelect: (value) => {
                setFilterEventType(value);
              },
              placeholder: "All event types",
            },
          ]}
          showDownloadButton={true}
          downloadFilename="Slate Report"
        />
      );
    }
  };

  useEffect(() => {
    getEventTypes();
    getTeams();
  }, [getEventTypes, getTeams]);

  useEffect(() => {
    getDetailedPaymentReport();
    getSummaryPaymentReport();
    getParticipationReport();
    getEventFeeAdjustmentsReport();
  }, [
    getDetailedPaymentReport,
    getSummaryPaymentReport,
    getParticipationReport,
    getEventFeeAdjustmentsReport,
  ]);

  useEffect(() => {
    setFilterTeam(null);
    setFilterEventType(null);
    setFilterStatus(null);
    setFilterReportStart(subMonths(new Date(), 3));
    setFilterReportEnd(new Date());
  }, [reportType]);

  return (
    <PageWrapper showHeader={true} showSidebar={true} title="Report">
      <Section>
        <DropdownSelect
          label={"Report type"}
          value={reportType}
          items={[
            {
              label: "Detailed payment report",
              value: "detailedPaymentReport",
            },
            {
              label: "Event fee adjustment report",
              value: "eventFeeAdjustmentsReport",
            },
            { label: "Summary payment report", value: "summaryPaymentReport" },
            {
              label: "Participation Report",
              value: "participationReport",
            },
          ]}
          onSelect={(value) => setReportType(value)}
        />
        {renderContent()}
      </Section>
    </PageWrapper>
  );
}

export default withStyles(styles)(withState(ReportsPage));
